import { CV, ARTIST } from "../data/portfolio";

const POSTERS = [
  { title: "Klaus", slug: "klaus", src: "https://media.themoviedb.org/t/p/w500/5u38V38eaIkGXAa4gyVHNUfAU4Z.jpg" },
  { title: "Space Jam: A New Legacy", slug: "space-jam-2", src: "https://media.themoviedb.org/t/p/w500/1eayKQzVdSpq89OwojwnUWdI1xT.jpg" },
  { title: "The Boy, the Mole, the Fox and the Horse", slug: "the-boy-the-mole-the-fox-and-the-horse", src: "https://media.themoviedb.org/t/p/w500/qm7VvyHVQ6SKICJQ2yZK1pyhWuj.jpg" },
  { title: "Animal Farm", slug: "animal-farm", src: "https://image.tmdb.org/t/p/w500/zEpnuQ4Vfa3GPeCei814x7UnQtb.jpg" },
  { title: "Pigeons", slug: "pigeons", src: "https://m.media-amazon.com/images/M/MV5BNTA2N2U3ZDAtZTc5Zi00MDFjLThlZTgtNjgzM2E4YjZkNjY5XkEyXkFqcGc@._V1_.jpg" },
];
import { Download } from "lucide-react";
import SocialLinks from "../components/SocialLinks";
import { useState } from "react";

function LogoCell({ src, studio }) {
  const [failed, setFailed] = useState(false);
  const shortName = studio.replace(/\s*\(.*?\)/, "").replace(" Animation", "").replace(" Television", "").replace(" Studios", "").replace(" Studio", "").replace(" Production", "");
  return (
    <div className="flex items-center justify-center h-10">
      {!failed ? (
        <img
          src={src}
          alt={studio}
          className="max-h-10 max-w-full object-contain"
          style={{ filter: "brightness(0) invert(1)", opacity: 0.55 }}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="text-[9px] uppercase tracking-[0.15em] text-center text-[var(--muted)] leading-tight">{shortName}</span>
      )}
    </div>
  );
}

export default function CVPage() {
  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 md:py-16" data-testid="cv-page">
      <div className="flex items-end justify-between gap-8 mb-8 reveal">
        <div>
          <div className="text-xs uppercase tracking-[0.4em] text-[var(--ember)] mb-4">Curriculum Vitae</div>
          <h1 className="font-serif text-6xl md:text-8xl leading-[0.85] text-[var(--paper)] italic">CV</h1>
        </div>
        <button
          data-testid="cv-print"
          onClick={() => window.print()}
          className="hidden md:inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[var(--paper)] link-underline"
        >
          <Download size={16}/> Print / Save PDF
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 mb-12">
        {/* Left: clients/collaborators logo grid */}
        <aside className="lg:col-span-5 reveal" data-testid="cv-clients-strip">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--muted)] mb-4">Selected Clients & Collaborators</div>
          <div className="border hairline p-5 md:p-6 lg:sticky lg:top-28">
            <div className="grid grid-cols-3 gap-4 items-center">
              {CV.flatMap(item =>
                item.logos
                  ? item.logos.map((src, i) => ({ src, studio: item.studio.split(" · ")[i] || item.studio }))
                  : item.logo
                  ? [{ src: item.logo, studio: item.studio }]
                  : []
              ).map(({ src, studio }) => (
                <LogoCell key={studio} src={src} studio={studio} />
              ))}
            </div>
          </div>
        </aside>

        {/* Right: CV work history */}
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--muted)] mb-4">Experience</div>
          <div className="space-y-0">
            {CV.filter(item => !item.logoOnly).map((item, idx) => (
              <div
                key={item.studio}
                className="py-5 border-t hairline reveal first:border-t-0 first:pt-0"
                style={{ transitionDelay: `${idx * 60}ms` }}
                data-testid={`cv-item-${idx}`}
              >
                <div className="font-serif text-xl md:text-2xl text-[var(--paper)] leading-tight mb-2">{item.studio}</div>
                {item.positions ? (
                  <div className="space-y-3 border-l border-[var(--ink-3)] pl-4">
                    {item.positions.map((pos) => (
                      <div key={pos.period}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--gold)]">{pos.period}</div>
                        <div className="mt-0.5 text-[var(--bone)] text-base italic">{pos.role}</div>
                        {pos.notes.length > 0 && (
                          <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--muted)]">
                            {pos.notes.map((n) => (
                              <li key={n} className="flex items-center gap-2">
                                <span className="text-[var(--ember)]">✦</span> {n}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--gold)]">{item.period}</div>
                    <div className="mt-0.5 text-[var(--bone)] text-base italic">{item.role}</div>
                    {item.notes.length > 0 && (
                      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--muted)]">
                        {item.notes.map((n) => (
                          <li key={n} className="flex items-center gap-2">
                            <span className="text-[var(--ember)]">✦</span> {n}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t hairline reveal">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--muted)] mb-3">Contact</div>
            <a href={`mailto:${ARTIST.email}`} className="font-serif text-2xl md:text-3xl italic text-[var(--paper)] link-underline">{ARTIST.email}</a>
          </div>
          <div data-testid="cv-social">
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--muted)] mb-3">Social</div>
            <SocialLinks/>
          </div>
        </div>
      </div>

      {/* Film poster strip */}
      <div className="overflow-hidden border-t hairline mt-12 -mx-6 lg:-mx-10">
        <div className="poster-strip flex gap-6" style={{ width: "max-content" }}>
          {[...POSTERS, ...POSTERS].map((p, i) => (
            <a
              key={i}
              href={`/animation/${p.slug}`}
              className="flex-shrink-0 relative block"
              style={{ width: "18vw" }}
              tabIndex={i < POSTERS.length ? 0 : -1}
            >
              <img
                src={p.src}
                alt={p.title}
                className="w-full h-auto block"
                style={{ filter: "sepia(0.3) contrast(1.05)" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[rgba(9,8,10,0.85)] via-transparent to-transparent" />
              <div className="absolute inset-0 bg-[var(--ember)] opacity-0 hover:opacity-10 transition-opacity duration-300" />
              <div className="absolute bottom-3 left-3 right-3 text-[8px] uppercase tracking-[0.25em] text-[var(--bone)] leading-tight font-sans">{p.title}</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
